/**
 * Consent outcome counts.
 *
 * An aggregate, identifier-free counter that answers one question: what share
 * of visitors actually opt in? Without it we cannot say how many visitors are
 * invisible to our analytics.
 *
 * Exactly four events exist, and the endpoint accepts nothing else:
 *   - banner_shown  the consent banner was on screen (once per page load)
 *   - accept_all    the visitor answered the banner with "Accept All"
 *   - reject_all    the visitor answered the banner with "Reject All"
 *   - gpc_detected  the browser sent a Global Privacy Control signal
 *                   (once per page load)
 *
 * Privacy properties, by design:
 *   - The request body is `{"event":"<name>"}` and nothing else: no URL, path,
 *     timestamp, session or visitor id.
 *   - The request is cookieless and referrer-less. It is a keepalive `fetch`
 *     with `credentials: "omit"` and `referrerPolicy: "no-referrer"` rather than
 *     `navigator.sendBeacon`, because a beacon always attaches the site's
 *     first-party cookies (for a returning visitor, their analytics ids) and a
 *     Referer carrying the full page URL, and cannot be told not to.
 *   - Nothing is written to cookies, localStorage or sessionStorage. The
 *     "once per page load" and "answered the banner" state lives in module
 *     variables, which vanish on the next full page load.
 *   - It fires without consent on purpose, because it carries no identifier.
 *     That is what lets it count the visitors we otherwise cannot see. The IP
 *     address and user agent still reach the host's network layer, as they do
 *     for any request; the app code never reads or stores them.
 *   - Delivery is best-effort: a blocked or failed request is dropped, not
 *     retried.
 *
 * The unit is page loads, not visitors. Crawlers, monitors and visitors who
 * reload without answering all add `banner_shown`, and a returning visitor with
 * a stored choice produces no event (only `gpc_detected` fires for them).
 *
 * Everything is behind NEXT_PUBLIC_CONSENT_METRICS (default off). With the flag
 * off nothing is sent and the endpoint answers 404.
 */

export const CONSENT_OUTCOME_EVENTS = [
    "banner_shown",
    "accept_all",
    "reject_all",
    "gpc_detected",
] as const;

export type ConsentOutcomeEvent = (typeof CONSENT_OUTCOME_EVENTS)[number];

export const CONSENT_OUTCOME_ENDPOINT = "/api/consent-outcome";

/**
 * True only when NEXT_PUBLIC_CONSENT_METRICS is "1" or "true" (any case). The
 * env access must stay a literal `process.env.NEXT_PUBLIC_*` expression so Next
 * inlines it. That happens at build time, in the server bundle too, so changing
 * the flag needs a redeploy.
 */
export function isConsentMetricsEnabled(): boolean {
    const flag = process.env.NEXT_PUBLIC_CONSENT_METRICS?.toLowerCase();
    return flag === "1" || flag === "true";
}

// Per-page-load state. Module variables reset on every full page load, which
// is the lifetime we want, and they never touch browser storage.
let bannerShownReported = false;
let bannerAwaitingAnswer = false;
let gpcReported = false;

function sendOutcome(event: ConsentOutcomeEvent): void {
    try {
        if (typeof fetch !== "function") return;
        fetch(CONSENT_OUTCOME_ENDPOINT, {
            method: "POST",
            body: JSON.stringify({ event }),
            keepalive: true,
            credentials: "omit",
            referrerPolicy: "no-referrer",
            cache: "no-store",
        }).catch(() => {
            // Best-effort: a blocked or failed count is dropped.
        });
    } catch {
        // Counting must never get in the way of the consent flow.
    }
}

/** The banner is on screen. Counted once per page load. */
export function reportBannerShown(): void {
    if (!isConsentMetricsEnabled() || bannerShownReported) return;
    bannerShownReported = true;
    bannerAwaitingAnswer = true;
    sendOutcome("banner_shown");
}

/**
 * The visitor made a consent choice. Only an answer to the banner on this page
 * load is counted, and only the two banner buttons (everything on, everything
 * off) map to an event. A later change from a settings panel, or a mixed
 * choice, is not a banner outcome and is not counted.
 */
export function reportConsentChoice(marketing: boolean, analytics: boolean): void {
    if (!isConsentMetricsEnabled() || !bannerAwaitingAnswer) return;
    bannerAwaitingAnswer = false;
    if (marketing && analytics) {
        sendOutcome("accept_all");
    } else if (!marketing && !analytics) {
        sendOutcome("reject_all");
    }
}

/** The browser sent a Global Privacy Control signal. Counted once per page load. */
export function reportGpcDetected(): void {
    if (!isConsentMetricsEnabled() || gpcReported) return;
    if (typeof navigator === "undefined") return;
    const signal = (navigator as Navigator & { globalPrivacyControl?: boolean })
        .globalPrivacyControl;
    if (signal !== true) return;
    gpcReported = true;
    sendOutcome("gpc_detected");
}
